import { VerticalFlexbox } from "@src/components/layout/Flexbox";
import { Sidebar } from "@src/components/layout/Sidebar";
import React, { useContext, useState } from "react";
import { CitationsProvider } from "./citationsProviders";

interface SidebarConfig {
  title: string;
  subtitle: string;
  name: string;
  nameBadge?: React.ReactNode;
  content: React.ReactNode;
}

interface SidebarProviderContextType {
  isOpen: boolean;
  toggleSidebar: (config: SidebarConfig) => void;
  openSidebar: (config: SidebarConfig) => void;
  closeSidebar: () => void;
}

export const SidebarProviderContext =
  React.createContext<SidebarProviderContextType | null>(null);

/**
 * Temporary v0 flag: hide the slide-out sidebar entirely while keeping the
 * provider, context, and `Sidebar` component wired up so we can flip this
 * back to `true` once the sidebar UX returns.
 */
const SHOW_SIDEBAR = false;

export const SidebarProvider = ({ children }: React.PropsWithChildren) => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<SidebarConfig | null>(null);

  const toggleSidebar = (nextConfig: SidebarConfig) => {
    if (isOpen) {
      setConfig(null);
      setIsOpen(false);
    } else {
      setConfig(nextConfig);
      setIsOpen(true);
    }
  };

  const openSidebar = (nextConfig: SidebarConfig) => {
    setConfig(nextConfig);
    setIsOpen(true);
  };

  const closeSidebar = () => {
    setIsOpen(false);
    setConfig(null);
  };

  return (
    <SidebarProviderContext.Provider
      value={{ isOpen, toggleSidebar, openSidebar, closeSidebar }}
    >
      <CitationsProvider>
        <VerticalFlexbox className="relative min-h-0 w-full flex-1">
          {children}
          {SHOW_SIDEBAR ? (
            <Sidebar
              isOpen={isOpen}
              name={config?.name}
              nameBadge={config?.nameBadge}
              title={config?.title}
              subtitle={config?.subtitle}
              content={config?.content}
              onClose={closeSidebar}
            />
          ) : null}
        </VerticalFlexbox>
      </CitationsProvider>
    </SidebarProviderContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarProviderContext);

  if (!context) {
    return {
      isOpen: false,
      toggleSidebar: () => {
        return void 0;
      },
      openSidebar: () => {
        return void 0;
      },
      closeSidebar: () => {
        return void 0;
      },
    };
  }

  return context;
};
